import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import '../styles/formStyles.css';
import { showAppAlert } from '../utils/alertService';

function TelegramQrCodeModal({ isOpen, onClose, telegramData, isLoading, error }) {
  const printableContentRef = useRef();

  if (!isOpen) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
          <h3>Carregando QR Code...</h3>
          <p>Aguarde enquanto preparamos seu QR code do Telegram.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
          <button onClick={onClose} className="modal-close-button">
            &times;
          </button>
          <h3>Erro ao Gerar QR Code</h3>
          <div style={{ padding: '15px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c00', margin: '20px 0' }}>
            <strong>Erro:</strong> {error}
          </div>
          <button onClick={onClose} className="button-style">
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!telegramData) {
    return null;
  }

  const handlePrint = () => {
    const content = printableContentRef.current.innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Imprimir QR Code Telegram</title>');
    printWindow.document.write(`
      <style>
        body { font-family: sans-serif; text-align: center; }
        a { word-break: break-all; }
      </style>
    `);
    printWindow.document.write('</head><body>');
    printWindow.document.write(content);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(telegramData.qrCodeUrl);
      showAppAlert('success', 'Link do Telegram copiado para a área de transferência!');
    } catch (err) {
      console.error('Falha ao copiar o link: ', err);
      showAppAlert('error', 'Falha ao copiar o link.');
    }
  };

  const handleShareWhatsApp = () => {
    const message = `Junte-se ao nosso grupo no Telegram!\n\n${telegramData.qrCodeUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(telegramData.qrCodeUrl)}`;
    window.open(facebookUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareTwitter = () => {
    const text = 'Junte-se ao nosso grupo no Telegram!';
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(telegramData.qrCodeUrl)}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareLinkedIn = () => {
    const title = 'Grupo Telegram';
    const summary = 'Junte-se ao nosso grupo no Telegram!';
    const linkedInUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(telegramData.qrCodeUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(summary)}`;
    window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
  };

  const handleShareEmail = () => {
    const subject = 'Convite para o Grupo Telegram';
    const body = `Olá!\n\nJunte-se ao nosso grupo no Telegram!\n\nClique no link: ${telegramData.qrCodeUrl}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: 'Grupo Telegram',
        text: 'Junte-se ao nosso grupo no Telegram!',
        url: telegramData.qrCodeUrl,
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Erro ao usar a API de compartilhamento: ', err);
        showAppAlert('error', 'Não foi possível abrir a janela de compartilhamento.');
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '500px', textAlign: 'center' }}>
        <button onClick={onClose} className="modal-close-button">
          &times;
        </button>
        <div ref={printableContentRef}>
          <h2>QR Code Telegram</h2>
          <p>Aponte a câmera do celular para o QR Code ou utilize o link abaixo para que seus clientes possam se juntar ao grupo do Telegram.</p>
          <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'center' }}>
            <QRCodeSVG value={telegramData.qrCodeUrl} size={256} level={"H"} />
          </div>
          <p><strong>ID do Grupo:</strong></p>
          <p style={{ wordBreak: 'break-all', fontFamily: 'monospace', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
            {telegramData.telegramGroupId}
          </p>
          <p><strong>Link para o Telegram:</strong></p>
          <p style={{ wordBreak: 'break-all' }}>
            <a href={telegramData.qrCodeUrl} target="_blank" rel="noopener noreferrer">{telegramData.qrCodeUrl}</a>
          </p>
        </div>
        <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
          <button 
            onClick={handleCopyLink} 
            className="button-style" 
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <i className="bi bi-copy" style={{ marginRight: '8px' }}></i> Copiar Link
          </button>

          {navigator.share && (
            <button 
              onClick={handleNativeShare} 
              className="button-style" 
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <i className="bi bi-share-fill" style={{ marginRight: '8px' }}></i> Compartilhar
            </button>
          )}

          {!navigator.share && (
            <>
              <button onClick={handleShareWhatsApp} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <i className="bi bi-whatsapp" style={{ marginRight: '8px' }}></i> WhatsApp
              </button>
              <button onClick={handleShareFacebook} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <i className="bi bi-facebook" style={{ marginRight: '8px' }}></i> Facebook
              </button>
              <button onClick={handleShareTwitter} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <i className="bi bi-twitter-x" style={{ marginRight: '8px' }}></i> X
              </button>
              <button onClick={handleShareLinkedIn} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <i className="bi bi-linkedin" style={{ marginRight: '8px' }}></i> LinkedIn
              </button>
              <button onClick={handleShareEmail} className="button-style" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <i className="bi bi-envelope-fill" style={{ marginRight: '8px' }}></i> Email
              </button>
            </>
          )}

          <button 
            onClick={handlePrint} 
            className="button-style"
            style={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <i className="bi bi-printer-fill" style={{ marginRight: '8px' }}></i> Imprimir QR Code
          </button>
        </div>
      </div>
    </div>
  );
}

export default TelegramQrCodeModal;
